import { useQuery } from '@tanstack/react-query';
import { SuggestedSettingsEnum, SuggestedSettingsType } from '@lp/features/RoutePlanner/types';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { hubQueries } from '@/queries/hub';
import {
  getCSSettingsText,
  getOrSettingsText,
  getRouteTypeSettingsText,
  getRSSettingsText,
  getStartLocationSettingsText,
  getStartTimeSettingsText,
  getTransportationTypeSettingsText,
  UNRESOLVED_HUB_LABEL,
} from './utils';

const HUB_SETTINGS = [SuggestedSettingsEnum.DeploymentHub, SuggestedSettingsEnum.ArrivalHub];

const HubCode = ({ hubId }: { hubId: string | null }): string => {
  const { data: hub } = useQuery({ ...hubQueries.item(hubId ?? ''), enabled: !!hubId });

  if (!hubId) return 'Any';

  return hub?.code || UNRESOLVED_HUB_LABEL;
};

const SETTINGS_CHIP_TITLE: Partial<Record<SuggestedSettingsEnum, string>> = {
  [SuggestedSettingsEnum.OR]: 'OR:',
  [SuggestedSettingsEnum.CS]: 'CS:',
  [SuggestedSettingsEnum.RS]: 'RS:',
  [SuggestedSettingsEnum.DeploymentHub]: 'Deployment Hub:',
  [SuggestedSettingsEnum.ArrivalHub]: 'Arrival Hub:',
  [SuggestedSettingsEnum.StartLocation]: 'Start Location:',
  [SuggestedSettingsEnum.StartTime]: 'Start Time:',
  [SuggestedSettingsEnum.OrganTransportationType]: 'DH-TXC transportation:',
};

const getSettingsChipValue = (
  item: SuggestedSettingsType,
  resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
): string | null => {
  switch (item.type) {
    case SuggestedSettingsEnum.OR:
      return getOrSettingsText(item.value);
    case SuggestedSettingsEnum.CS:
      return getCSSettingsText(item.value);
    case SuggestedSettingsEnum.RS:
      return getRSSettingsText(item.value);
    case SuggestedSettingsEnum.StartLocation:
      return getStartLocationSettingsText(item.value, resolveSubdivisionName);
    case SuggestedSettingsEnum.StartTime:
      return getStartTimeSettingsText(item.value);
    case SuggestedSettingsEnum.OrganTransportationType:
      return getTransportationTypeSettingsText(item.value);
    default:
      return null;
  }
};

type SettingsChipLabelProps = {
  item: SuggestedSettingsType;
  resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined;
};

export const SettingsChipLabel = ({ item, resolveSubdivisionName }: SettingsChipLabelProps) => {
  if (item.type === SuggestedSettingsEnum.RouteType) {
    const isFullRoute = item.value === RouteTypeSettingsEnum.Full;

    return (
      <>
        <span className="text-base-5 text-s-sb">{isFullRoute ? 'Full Route' : 'Partial:'}</span>
        {!isFullRoute && <span className="text-base-5 text-s-r">{getRouteTypeSettingsText(item.value)}</span>}
      </>
    );
  }

  const title = SETTINGS_CHIP_TITLE[item.type];

  if (!title) return null;

  const isHubSetting = HUB_SETTINGS.includes(item.type);

  return (
    <>
      <span className="text-base-5 text-s-sb">{title}</span>
      <span className="text-base-5 text-s-r">
        {isHubSetting ? (
          <HubCode hubId={item.value as string | null} />
        ) : (
          getSettingsChipValue(item, resolveSubdivisionName)
        )}
      </span>
    </>
  );
};
